import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Box, IconButton, Menu, Stack, Tooltip} from '@mui/material';
import './AgentChat.css';
import {History, Plus} from 'lucide-react';
import {ConversationHistory} from './ConversationHistory';
import {DrawerHeaderTitle} from '../common/DrawerHeader';
import {useNavigationGuard} from '../../context/NavigationGuardContext';

export const ConversationHeader = ({
  conversationId,
  conversations,
  clearCurrentConversation,
  createNewConversation,
  deleteConversation,
  renameConversation,
  loadConversation,
  onBack,
  isStreaming,
  shouldShowEmptyMessage,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const {runGuarded} = useNavigationGuard();

  const getCurrentConversation = () => {
    if (!conversationId) return 'New Chat';
    const conversation = conversations.find((c) => c.id === conversationId);
    return conversation || {title: 'New Chat'};
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Function that properly loads a conversation by ID - passed to ConversationHistory component
  const handleLoadConversation = (id) => {
    runGuarded(() => {
      // First clear the current conversation state
      clearCurrentConversation();

      // Then call the actual loadConversation function provided by the parent component
      if (id) {
        loadConversation(id);
      }

      // Close the conversation history menu
      handleMenuClose();
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <DrawerHeaderTitle
        title={getCurrentConversation().title || 'New Chat'}
        onBack={onBack}
      />
      <Stack
        direction="row"
        alignItems="center"
        spacing={0}
        sx={{ml: 'auto'}}
        className="header-icons-group"
      >
        <Tooltip title="New Chat">
          <span>
            <IconButton
              onClick={createNewConversation}
              size="small"
              disabled={isStreaming || shouldShowEmptyMessage}
              sx={{
                opacity: isStreaming || shouldShowEmptyMessage ? 0.5 : 1,
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              <Plus />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Chat History">
          <span>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              disabled={isStreaming}
              sx={{
                opacity: isStreaming ? 0.5 : 1,
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              <History />
            </IconButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          classes={{
            paper: 'conversation-menu-paper',
          }}
          transformOrigin={{horizontal: 'right', vertical: 'top'}}
          anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
        >
          <ConversationHistory
            conversationId={conversationId}
            conversations={conversations}
            loadConversation={handleLoadConversation}
            deleteConversation={deleteConversation}
            renameConversation={renameConversation}
            closeMenu={handleMenuClose}
          />
        </Menu>
      </Stack>
    </Box>
  );
};

ConversationHeader.propTypes = {
  conversationId: PropTypes.string,
  conversations: PropTypes.array.isRequired,
  clearCurrentConversation: PropTypes.func.isRequired,
  createNewConversation: PropTypes.func.isRequired,
  deleteConversation: PropTypes.func.isRequired,
  renameConversation: PropTypes.func.isRequired,
  loadConversation: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isStreaming: PropTypes.bool,
  shouldShowEmptyMessage: PropTypes.bool,
};
